/* Admin persona-assignment surface (persona-foundation Task 4).
 *
 * There is NO pre-existing admin user-management panel in the hub —
 * `admin-users.html` (design/ux-audit) was only ever a design mock, never
 * built; the hub only ever fetched `GET /users` for the assign-picker
 * roster (state/workspace.ts's `ws.users`, consumed by ws/mentions.ts). So
 * this module IS the admin surface, built minimal/functional per the task
 * brief (a full design pass is a later, separate task) — reachable only for
 * `AUTH.user.role === 'admin'` via a "Users…" item in the identity-chip
 * account menu (ws/board.ts's identityMenu).
 *
 * Two exported pieces, deliberately separable:
 *  - `renderPersonaEditor` — the per-user editor widget: multi-select
 *    persona checkboxes, a department picker shown ONLY when `management`
 *    is checked, and a primary-persona picker constrained to the currently
 *    checked personas. Local-repaint convention (mutable closure state +
 *    `render()` back into the same host on every change) — the same
 *    pattern ws/checklist.ts's checklistPanel and ws/dialogs.ts's modals
 *    use; this codebase has no reactive component framework. Takes `onSave`
 *    as an injected callback (not a direct api/users.ts import) so it is
 *    unit-testable with a plain `vi.fn()` — no module mocking needed,
 *    matching how the rest of this suite stubs Workspace methods rather
 *    than mocking modules (see tests/unit/savedFilters.test.ts).
 *  - `openUsersPanel` — the modal list (mirrors ws/dialogs.ts's
 *    exportCompleted: a detached `.modal-bk`/`.modal` host, appended once,
 *    torn down via `bk.remove()`). Fetches the roster fresh via
 *    api/users.ts's getUsers() (NOT ws.users — that copy is fetched once
 *    per client-board load and would show stale chips after an edit here).
 *    "Edit" opens a second, stacked modal hosting renderPersonaEditor for
 *    that one user; a successful save patches the row in place and closes
 *    the inner modal.
 *
 * Tokens only (--sp-*, --paper*, --ink, --line, --r-*) — styles in
 * hub/src/styles/workspace.css. Persona chips reuse `.pill-meta` (app.css)
 * per the design spec's "Admin panel" section. All user-supplied text
 * (name/email) goes through plain lit child/attribute bindings, which
 * lit-html escapes by default (wiki.ts conventions #3/#4) — no manual esc().
 */
import { html, nothing, render, type TemplateResult } from 'lit';
import type { AuthUser } from '../lib/auth.ts';
import type { PersonaRow } from '../lib/personas.ts';
import { capacityKey, capacityLabel } from '../lib/personas.ts';
import { getUsers, setUserPersonas } from '../api/users.ts';
import type { Workspace } from '../state/workspace.ts';

export const PERSONA_OPTIONS = ['bizdev', 'am', 'writer', 'management'] as const;
export const MGMT_DEPARTMENTS = ['company', 'bizdev', 'am', 'writer'] as const;

interface EditorState {
  personas: Set<string>;
  // management's departments — a user can hold MULTIPLE (e.g. CEO AND head
  // of bizdev at once, the design's motivating example: management/company
  // + management/bizdev). Empty until at least one is chosen.
  departments: Set<string>;
  primary: string;    // capacity key ('bizdev' | 'am' | 'writer' | 'management:<dept>')
}

function initialState(user: AuthUser): EditorState {
  const rows = user.personas || [];
  return {
    personas: new Set(rows.map((r) => r.persona)),
    departments: new Set(rows.filter((r) => r.persona === 'management').map((r) => r.department)),
    primary: user.primary_persona || '',
  };
}

/** The capacity keys the currently-checked personas actually resolve to —
 * `management` contributes one key PER selected department (an unset/empty
 * department set isn't a real, storable capacity yet). Mirrors
 * lib/personas.ts's heldCapacities() shape but over in-progress editor state
 * rather than a saved user. */
function heldKeys(state: EditorState): string[] {
  const keys: string[] = [];
  for (const p of PERSONA_OPTIONS) {
    if (!state.personas.has(p)) continue;
    if (p === 'management') { for (const d of state.departments) keys.push(capacityKey(p, d)); }
    else keys.push(p);
  }
  return keys;
}

/** Renders (and re-renders, on every field change) the persona editor for
 * one user into `host`. `onSave` performs the PATCH (injected — see module
 * doc); `onSaved` runs after a successful save with the updated user. */
export function renderPersonaEditor(
  host: HTMLElement,
  user: AuthUser,
  onSave: (personas: PersonaRow[], primary: string) => Promise<{ user: AuthUser }>,
  onSaved?: (updated: AuthUser) => void,
): void {
  const state = initialState(user);
  let saving = false;
  let error: string | null = null;

  const paint = () => render(template(), host);

  const togglePersona = (p: string, checked: boolean) => {
    if (checked) state.personas.add(p); else state.personas.delete(p);
    if (p === 'management' && !checked) state.departments.clear();
    if (!heldKeys(state).includes(state.primary)) state.primary = '';
    error = null;
    paint();
  };

  const toggleDepartment = (dept: string, checked: boolean) => {
    if (checked) state.departments.add(dept); else state.departments.delete(dept);
    if (!heldKeys(state).includes(state.primary)) state.primary = '';
    error = null;
    paint();
  };

  const setPrimary = (key: string) => { state.primary = key; error = null; paint(); };

  const save = async () => {
    // Checked before the empty-keys check below: management alone with zero
    // departments selected yields zero held keys too, and would otherwise be
    // misreported as "select at least one persona" instead of the actionable
    // department-specific message.
    if (state.personas.has('management') && !state.departments.size) { error = 'pick at least one management department'; paint(); return; }
    const keys = heldKeys(state);
    if (!keys.length) { error = 'select at least one persona'; paint(); return; }
    if (!state.primary || !keys.includes(state.primary)) { error = 'pick a primary persona'; paint(); return; }
    const personas: PersonaRow[] = [
      ...[...state.personas].filter((p) => p !== 'management').map((p) => ({ persona: p, department: '' })),
      ...[...state.departments].map((d) => ({ persona: 'management', department: d })),
    ];
    saving = true; error = null; paint();
    try {
      const { user: updated } = await onSave(personas, state.primary);
      saving = false; paint();
      if (onSaved) onSaved(updated);
    } catch (e) {
      saving = false; error = e instanceof Error ? e.message : 'save failed'; paint();
    }
  };

  const template = (): TemplateResult => {
    const keys = heldKeys(state);
    return html`<div class="wsuedit">
      <div class="wsuedit-personas" role="group" aria-label="personas">
        ${PERSONA_OPTIONS.map((p) => html`
          <label class="wsuedit-opt"><input type="checkbox" .checked=${state.personas.has(p)}
            @change=${(e: Event) => togglePersona(p, (e.target as HTMLInputElement).checked)}> ${p}</label>`)}
      </div>
      ${state.personas.has('management') ? html`
        <div class="wsuedit-dept">management departments
          <div class="wsuedit-depts" role="group" aria-label="management departments">
            ${MGMT_DEPARTMENTS.map((d) => html`
              <label class="wsuedit-opt"><input type="checkbox" .checked=${state.departments.has(d)}
                @change=${(e: Event) => toggleDepartment(d, (e.target as HTMLInputElement).checked)}> ${capacityLabel(capacityKey('management', d))}</label>`)}
          </div>
        </div>` : nothing}
      <label class="wsuedit-primary">primary
        <select @change=${(e: Event) => setPrimary((e.target as HTMLSelectElement).value)}>
          <option value="" ?selected=${!state.primary}>choose…</option>
          ${keys.map((k) => html`<option value="${k}" ?selected=${k === state.primary}>${capacityLabel(k)}</option>`)}
        </select>
      </label>
      ${error ? html`<div class="wsuedit-err">${error}</div>` : nothing}
      <button class="wsmini wsuedit-save" ?disabled=${saving} @click=${save}>${saving ? 'Saving…' : 'Save'}</button>
    </div>`;
  };

  paint();
}

function personaChip(p: PersonaRow): TemplateResult {
  return html`<span class="pill-meta">${p.persona === 'management' && p.department ? `management · ${p.department}` : p.persona}</span>`;
}

/** Opens the admin-only Users list modal, `GET /users`-backed, with a
 * per-row "Edit" action. Caller (identityMenu) must already have gated on
 * `AUTH.user.role === 'admin'` — this function does not re-check role
 * (the backend PATCH enforces it server-side regardless). */
export async function openUsersPanel(ws: Workspace): Promise<void> {
  const bk = document.createElement('div');
  bk.className = 'modal-bk';
  bk.addEventListener('mousedown', (e) => { if (e.target === bk) bk.remove(); });

  let users: AuthUser[] = [];
  let loading = true;
  let loadError: string | null = null;

  const paint = () => render(template(), bk);

  const openEditor = (u: AuthUser) => {
    const bk2 = document.createElement('div');
    bk2.className = 'modal-bk';
    render(html`<div class="modal wsuedit-modal" style="max-width:380px">
      <h2 style="font-size:16px">Edit personas</h2>
      <div class="mh">${u.name} · ${u.email}</div>
      <div class="wsuedit-host"></div>
      <div class="row"><button class="cancel" @click=${() => bk2.remove()}>Close</button></div>
    </div>`, bk2);
    document.body.appendChild(bk2);
    bk2.addEventListener('mousedown', (e) => { if (e.target === bk2) bk2.remove(); });
    const editHost = bk2.querySelector('.wsuedit-host') as HTMLElement;
    renderPersonaEditor(
      editHost, u,
      (personas, primary) => setUserPersonas(u.id, personas, primary),
      (updated) => {
        ws.toast('Personas updated.', 'ok');
        const i = users.findIndex((x) => x.id === updated.id);
        if (i >= 0) users[i] = updated;
        paint();
        bk2.remove();
      },
    );
  };

  const template = (): TemplateResult => html`<div class="modal wsusers-modal" style="max-width:640px">
    <h2 style="font-size:16px">Users</h2>
    <div class="mh">Assign personas — the job-focus each teammate lands on by default at sign-in. Role (admin/member) is managed separately and unchanged here.</div>
    ${loading ? html`<div class="mh">Loading…</div>`
      : loadError ? html`<div class="wsuedit-err">${loadError}</div>`
      : !users.length ? html`<div class="mh">No users yet.</div>`
      : html`<div class="wsusers-list">${users.map((u) => html`
        <div class="wsusers-row">
          <div class="wsusers-who">
            <b>${u.name}</b>
            <span class="wsusers-email">${u.email}</span>
            <span class="pill-meta mono">${u.role}</span>
          </div>
          <div class="wsusers-chips">
            ${u.personas && u.personas.length ? u.personas.map(personaChip) : html`<span class="pill-meta">no persona</span>`}
          </div>
          <button class="wsmini" @click=${() => openEditor(u)}>Edit</button>
        </div>`)}</div>`}
    <div class="row"><button class="cancel" @click=${() => bk.remove()}>Close</button></div>
  </div>`;

  paint();
  document.body.appendChild(bk);
  try {
    users = await getUsers();
  } catch (e) {
    loadError = 'Could not load users: ' + (e instanceof Error ? e.message : String(e));
  }
  loading = false;
  paint();
}
